import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";

// Floating customer search (portal overlay): never pushes or hides the rest of the form.
export function CustomerCombobox({ value, options, onSelect, error }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [editing, setEditing] = useState(!value);
  const inputRef = useRef(null);
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ["cust-search", dq], enabled: dq.length >= 1,
    queryFn: async () => (await api.get(`/customers?search=${encodeURIComponent(dq)}&limit=20`)).data,
  });
  const selected = options.find((c) => c.id === value);
  const results = dq ? (data?.items || []) : options.slice(0, 20);
  useEffect(() => { setActive(0); }, [dq]);

  const pick = (c) => { onSelect(c.id); setOpen(false); setQ(""); setEditing(false); };
  const close = () => { setOpen(false); if (value) setEditing(false); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (open && results[active]) { e.preventDefault(); pick(results[active]); } }
    else if (e.key === "Escape") { if (open) { e.preventDefault(); e.stopPropagation(); close(); } }
  };

  return (
    <div className="sm:col-span-2" data-field="customer_id">
      <Label htmlFor="bk-customer-input" className="text-xs text-slate-500 mb-1 block">Customer *</Label>
      {selected && !editing ? (
        <button type="button" data-testid="bk-customer" onClick={() => { setEditing(true); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="w-full flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left">
          <span>{selected.name} · {selected.mobile}</span><span className="text-xs text-slate-400">Change</span>
        </button>
      ) : (
        <Popover open={open && (q.length > 0 || results.length > 0)} onOpenChange={(o) => { if (!o) close(); }}>
          <PopoverAnchor asChild>
            <Input id="bk-customer-input" ref={inputRef} autoFocus={!value} value={q} role="combobox" aria-expanded={open} aria-controls="bk-customer-results"
              aria-autocomplete="list" aria-activedescendant={open && results[active] ? `bk-cust-opt-${results[active].id}` : undefined}
              aria-invalid={!!error} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
              placeholder="Search name or mobile…" data-testid="bk-customer-search" className={`bg-white ${error ? "border-rose-400" : ""}`} />
          </PopoverAnchor>
          <PopoverContent align="start" sideOffset={4} collisionPadding={8}
            onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => { if (inputRef.current?.contains(e.target)) e.preventDefault(); }}
            className="p-0 w-[var(--radix-popover-trigger-width)] bg-white max-h-64 overflow-y-auto z-[60]">
            <div id="bk-customer-results" role="listbox" aria-label="Customers" data-testid="bk-customer-results">
              {isFetching && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
              {!isFetching && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400" data-testid="bk-customer-noresult">No customers found</div>}
              {results.map((c, i) => (
                <button type="button" key={c.id} id={`bk-cust-opt-${c.id}`} role="option" aria-selected={i === active} tabIndex={-1}
                  data-testid={`bk-customer-opt-${c.id}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)}
                  className={`w-full text-left px-3 py-2 text-sm ${i === active ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                  <span className="font-medium text-slate-900">{c.name}</span> <span className="text-xs text-slate-500">· {c.mobile}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {error && <p className="mt-1 text-xs text-rose-600" data-testid="bk-err-customer_id">{error}</p>}
    </div>
  );
}
